import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface StickyConfig {
  title?: string;
  offset?: number;
  hasButton?: boolean;
}

@Directive({
  selector: '[boelsSticky]',
  standalone: true,
})
export class StickyDirective implements OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);

  private stickyDiv: HTMLElement | null = null;

  @Input() stickyConfig: StickyConfig;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    const correctedPosition = this.calculatePosition();

    if (correctedPosition < 0) {
      this.createAndAppendStickyDiv();
    } else {
      this.removeStickyDiv();
    }
  }

  private calculatePosition(): number {
    if (!this.el.nativeElement) return 0;
    const elementPosition = this.el.nativeElement.getBoundingClientRect();
    return elementPosition.bottom + (this.stickyConfig.offset ?? -25);
  }

  private createAndAppendStickyDiv(): void {
    if (!this.stickyDiv) {
      this.stickyDiv = this.renderer.createElement('div');
      this.renderer.addClass(this.stickyDiv, 'sticky');

      const contentDiv = this.renderer.createElement('div');
      this.renderer.addClass(contentDiv, 'content');

      const pageTitle = this.renderer.createElement('h3');
      const text = this.renderer.createText(
        this.stickyConfig.title ?? this.document.title,
      );
      this.renderer.appendChild(pageTitle, text);
      const clonedElement = this.el.nativeElement.cloneNode(true);

      if (this.stickyConfig.hasButton) {
        const button = clonedElement.querySelector('button');
        if (button) {
          button.classList.add('sticky-ignore');
          button.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const originalButton =
              this.el.nativeElement.querySelector('button');
            if (originalButton) {
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: this.document.defaultView,
              });
              originalButton.classList.add('clicked-from-sticky');
              originalButton.dispatchEvent(clickEvent);
              originalButton.classList.remove('clicked-from-sticky');
            }
          });
        }
      }

      [pageTitle, clonedElement].forEach((element) => {
        this.renderer.appendChild(contentDiv, element);
      });

      this.renderer.appendChild(this.stickyDiv, contentDiv);
      this.renderer.appendChild(this.document.body, this.stickyDiv);
    }
  }

  private removeStickyDiv(): void {
    if (this.stickyDiv) {
      this.renderer.removeChild(this.document.body, this.stickyDiv);
      this.stickyDiv = null;
    }
  }

  ngOnDestroy() {
    this.removeStickyDiv();
  }
}
